// src/pages/Report/ReportGenerator.js

import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Stack,
  Paper,
  Divider,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useReportGenerator } from "../../hooks/useReportGenerator";
import FullScreenSpinner from "../../components/FullScreenSpinner";

const ReportGenerator = () => {
  const {
    reportes,
    reportesMap,
    reporteSeleccionado,
    setReporteSeleccionado,
    filtrosBackend,
    data,
    consultar,
    exportarDirecto,
    renderFiltro,
    table,
    limiteRegistros,
    setLimiteRegistros,
    loading,
  } = useReportGenerator();

  const [search, setSearch] = useState("");

  const reportesFiltrados = useMemo(
    () =>
      reportes.filter((r) =>
        r.nombre.toLowerCase().includes(search.toLowerCase())
      ),
    [reportes, search]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {loading && <FullScreenSpinner text="Consultando registros en base de datos. Por favor, espere..." />}
      <Box sx={{ 
        height: "calc(100vh - (var(--navbar-height) + var(--footer-height)))",
        width: "100%",
        display: "flex", 
        flexDirection: "column",
        overflow: "hidden",
        boxSizing: "border-box",
      }}>
        <Paper sx={{ 
          flex: 1, 
          m: 0.5,
          display: "flex", 
          minHeight: 0,
          overflow: "hidden",
        }}>
          <Stack direction="row" spacing={0.5} sx={{ flex: 1, minHeight: 0, overflow: "hidden", p: 0.5 }}>
            <Box
              sx={{
                width: 220,
                borderRight: "1px solid #ddd",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <TextField
                size="small"
                placeholder="Buscar reporte..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                sx={{ 
                  mb: 0.5, 
                  flexShrink: 0,
                  '& .MuiInputBase-input': { // 👈 Agregar variable de font-size
                    fontSize: 'var(--app-font-size)',
                  },
                }}
              />
              <List 
                dense
                sx={{ 
                  flex: 1, 
                  overflow: "auto", 
                  minHeight: 0,
                  py: 0,
                }}
              >
                {reportesFiltrados.map((r) => (
                  <ListItemButton
                    dense
                    key={r.reporte_id}
                    selected={
                      reporteSeleccionado?.reporte_id === r.reporte_id
                    }
                    onClick={() =>
                      setReporteSeleccionado(reportesMap.get(r.reporte_id))
                    }
                    sx={{ py: 0.5 }}
                  >
                    <ListItemText
                      primary={r.nombre}
                      secondary={r.descripcion}
                      primaryTypographyProps={{ // 👈 Usar variable
                        variant: "body2",
                        sx: { fontSize: 'var(--app-font-size)' },
                      }}
                      secondaryTypographyProps={{ // 👈 Usar variable
                        variant: "caption",
                        sx: { fontSize: 'calc(var(--app-font-size) * 0.85)' }, // 85% del tamaño base
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <Box sx={{ flexShrink: 0 }}>
                <Stack spacing={0.5}>
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                    <Button
                      variant="contained"
                      size="small"
                      onClick={consultar}
                      disabled={!reporteSeleccionado}
                      sx={{ fontSize: 'var(--app-font-size)' }} // 👈 Agregar
                    >
                      Consultar
                    </Button>
                    
                    
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <Select
                        size="small"
                        value={limiteRegistros}
                        onChange={(e) => setLimiteRegistros(Number(e.target.value))}
                        sx={{
                          height: 'calc(var(--app-font-size) * 2.5)',
                          fontSize: 'var(--app-font-size)',
                          '& .MuiSelect-select': {
                            py: 0.5,
                            fontSize: 'var(--app-font-size)',
                          },
                        }}
                      >
                        <MenuItem value={1000} sx={{ fontSize: 'var(--app-font-size)' }}>
                          1.000 registros
                        </MenuItem>
                        <MenuItem value={10000} sx={{ fontSize: 'var(--app-font-size)' }}>
                          10.000 registros
                        </MenuItem>
                        <MenuItem value={50000} sx={{ fontSize: 'var(--app-font-size)' }}>
                          50.000 registros
                        </MenuItem>
                        <MenuItem value={0} sx={{ fontSize: 'var(--app-font-size)' }}>
                          Sin límite de registros
                        </MenuItem>
                      </Select>
                    </FormControl>

                    <Button
                      size="small"
                      onClick={() => exportarDirecto("csv")}
                      disabled={!reporteSeleccionado}
                      sx={{ fontSize: 'var(--app-font-size)' }}
                    >
                      CSV
                    </Button>

                    <Button
                      size="small"
                      onClick={() => exportarDirecto("excel")}
                      disabled={!reporteSeleccionado}
                      sx={{ fontSize: 'var(--app-font-size)' }}
                    >
                      Excel
                    </Button>

                    <Button
                      size="small"
                      onClick={() => exportarDirecto("pdf")}
                      disabled={!reporteSeleccionado}
                      sx={{ fontSize: 'var(--app-font-size)' }}
                    >
                      PDF
                    </Button>
                    
                  </Stack>

                  <Divider />

                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {filtrosBackend.map((f) =>
                      renderFiltro(f, DatePicker, TextField, Stack)
                    )}
                  </Stack>
                </Stack>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,
                  mt: 0.5,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <MaterialReactTable table={table} />
              </Box>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default ReportGenerator;